import { LanguageKeys } from 'src/common/constants/languages';
import { normalizeParentId } from 'src/helpers/publicPageMenuTree';
import { IBasePublicPage, IPublicPage } from 'src/interfaces/PublicPage';

type PublicPageRef =
  | string
  | { id?: string | { _bsontype?: string }; _id?: string; _bsontype?: string; $oid?: string }
  | null
  | undefined;

/** Normalize Mongo/API refs (ObjectId string or populated `{ id }`) to a plain id string. */
export function normalizePublicPageRefId(ref: PublicPageRef): string | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'string') {
    const trimmed = ref.trim();
    return trimmed || undefined;
  }
  if (typeof ref === 'object') {
    if ('$oid' in ref && ref.$oid) return String(ref.$oid);
    if (ref._bsontype === 'ObjectId') return String(ref);
    if (typeof ref.id === 'string') return ref.id;
    const nested = ref.id;
    if (nested && typeof nested === 'object' && '_bsontype' in nested && nested._bsontype === 'ObjectId') {
      return String(nested);
    }
    if (ref._id != null) return String(ref._id);
  }
  return undefined;
}

/** Resolve redirectTo (id or page key) to a page id for selects and routing. */
export function resolveRedirectToPageId(
  redirectTo: PublicPageRef,
  pages: IPublicPage[],
  pagesByKey?: Record<string, IPublicPage>,
): string | undefined {
  const byId = normalizePublicPageRefId(redirectTo);
  if (byId && pages.some(p => p.id === byId)) return byId;
  if (typeof redirectTo === 'string') {
    const key = redirectTo.trim();
    const byKey = pagesByKey?.[key];
    if (byKey) return byKey.id;
  }
  return byId;
}

const LANGUAGE_KEYS: LanguageKeys[] = [LanguageKeys.en, LanguageKeys.hu, LanguageKeys.ro];

export function normalizePath(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function indexPublicPagesById(publicPages: IPublicPage[]): Record<string, IPublicPage> {
  const map: Record<string, IPublicPage> = {};
  for (const page of publicPages) {
    if (page.id) map[page.id] = page;
  }
  return map;
}

export function resolvePageByPath(
  pathname: string,
  publicPages: IPublicPage[],
  options?: { includeUnpublished?: boolean },
): IPublicPage | undefined {
  const normalized = normalizePath(pathname);
  const byId = indexPublicPagesById(publicPages);
  return publicPages.find(p => {
    if (!options?.includeUnpublished && !p.published) return false;
    if (p.pathAliases?.includes(normalized)) return true;
    return LANGUAGE_KEYS.some(
      lang => getPathForLanguage(p, lang, byId) === normalized,
    );
  });
}

export function getPathForLanguage(
  page: IPublicPage,
  lang: LanguageKeys,
  publicPagesById?: Record<string, IPublicPage>,
): string {
  const storedPath = normalizePath(page.paths[lang] || page.paths.en);
  const parent = getParentPublicPage(page, publicPagesById);
  if (!parent) return storedPath;

  const parentPath = normalizePath(parent.paths[lang] || parent.paths.en);
  if (!parentPath) return storedPath;

  if (storedPath === parentPath || storedPath.startsWith(`${parentPath}/`)) {
    return storedPath;
  }

  const segment = getPathSegmentForEditor(storedPath, parent, lang);
  return buildFullPathFromSegment(segment, parent, lang);
}

/** When the current URL maps to exactly one locale, return it so i18n can stay in sync with the path. */
export function resolveLanguageByPath(
  pathname: string,
  publicPages: IPublicPage[],
): LanguageKeys | undefined {
  const page = resolvePageByPath(pathname, publicPages);
  if (!page) return undefined;

  const normalized = normalizePath(pathname);
  const byId = indexPublicPagesById(publicPages);
  const matching = LANGUAGE_KEYS.filter(
    lang => getPathForLanguage(page, lang, byId) === normalized,
  );

  return matching.length === 1 ? matching[0] : undefined;
}

export function getPublicPageMenuLabel(
  page: Pick<IPublicPage, 'key' | 'menu'>,
  lang: LanguageKeys,
): string {
  const label = page.menu.labels[lang]?.trim();
  if (label) return label;
  const fallback = page.menu.labels.en?.trim();
  if (fallback) return fallback;
  return page.key;
}

/** Next `menu.sortOrder` so a new page appears last among siblings (same parent). */
export function nextMenuSortOrder(
  pages: IPublicPage[],
  parentId?: string | null,
): number {
  const pid = normalizeParentId(parentId);
  const siblings = pages.filter(p => normalizeParentId(p.menu?.parentId) === pid);
  if (siblings.length === 0) return 0;
  return Math.max(...siblings.map(p => p.menu.sortOrder ?? 0)) + 1;
}

export function slugifyLabel(label: string): string {
  if (!label) return '';
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Strip slashes for a single URL segment (no internal slashes). */
function normalizePathSegment(segment: string): string {
  return segment.trim().replace(/^\/+|\/+$/g, '');
}

export function getParentPublicPage(
  page: Pick<IBasePublicPage, 'menu'>,
  publicPagesById?: Record<string, IPublicPage>,
): IPublicPage | undefined {
  const parentId = normalizePublicPageRefId(page.menu?.parentId);
  if (!parentId || !publicPagesById) return undefined;
  return publicPagesById[parentId];
}

/** CMS pages that can be chosen as a menu group's default redirect (direct children only). */
export function getGroupRedirectChildPages(
  pages: IPublicPage[],
  group: Pick<IBasePublicPage, 'id' | 'kind' | 'paths'> | undefined,
  currentRedirectTo?: PublicPageRef,
  pagesByKey?: Record<string, IPublicPage>,
): IPublicPage[] {
  if (!group || group.kind !== 'group') return [];

  const gid = normalizePublicPageRefId(group.id);
  let children = gid
    ? pages.filter(
        p => p.kind === 'page' && normalizeParentId(p.menu.parentId) === gid,
      )
    : [];

  if (children.length === 0) {
    const groupPath = normalizePath(group.paths?.en || group.paths?.hu || group.paths?.ro || '');
    if (groupPath) {
      children = pages.filter(p => {
        if (p.kind !== 'page') return false;
        const childPath = normalizePath(p.paths?.en || p.paths?.hu || p.paths?.ro || '');
        return childPath.startsWith(`${groupPath}/`);
      });
    }
  }

  children.sort((a, b) => (a.menu.sortOrder ?? 0) - (b.menu.sortOrder ?? 0));

  const redirectId = resolveRedirectToPageId(currentRedirectTo ?? '', pages, pagesByKey);
  if (redirectId && !children.some(p => p.id === redirectId)) {
    const current = pages.find(p => p.id === redirectId);
    if (current) return [current, ...children];
  }

  return children;
}

/** Path segment shown in the editor (child suffix only when nested under a parent). */
function getPathSegmentForEditor(
  storedPath: string,
  parent: IPublicPage | undefined,
  lang: LanguageKeys,
): string {
  const full = normalizePath(storedPath);
  if (!full) return '';

  if (!parent) {
    return full.replace(/^\//, '');
  }

  const parentPath = normalizePath(parent.paths[lang] || parent.paths.en);
  if (parentPath && (full === parentPath || full.startsWith(`${parentPath}/`))) {
    return full.slice(parentPath.length).replace(/^\//, '');
  }

  const parts = full.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

function buildFullPathFromSegment(
  segment: string,
  parent: IPublicPage | undefined,
  lang: LanguageKeys,
): string {
  const seg = normalizePathSegment(segment);
  if (!seg) return '';

  if (!parent) {
    return normalizePath(seg);
  }

  const parentPath = normalizePath(parent.paths[lang] || parent.paths.en);
  if (!parentPath) {
    return normalizePath(seg);
  }

  return normalizePath(`${parentPath}/${seg}`);
}

export function pathSegmentsFromPage(
  page: Pick<IBasePublicPage, 'paths' | 'menu'>,
  publicPagesById?: Record<string, IPublicPage>,
): Record<LanguageKeys, string> {
  const parent = getParentPublicPage(page, publicPagesById);
  return {
    [LanguageKeys.en]: getPathSegmentForEditor(page.paths.en, parent, LanguageKeys.en),
    [LanguageKeys.hu]: getPathSegmentForEditor(page.paths.hu, parent, LanguageKeys.hu),
    [LanguageKeys.ro]: getPathSegmentForEditor(page.paths.ro, parent, LanguageKeys.ro),
  };
}

export function resolveFullPathsFromSegments(
  segments: Record<LanguageKeys, string>,
  page: Pick<IBasePublicPage, 'menu'>,
  publicPagesById?: Record<string, IPublicPage>,
): Record<LanguageKeys, string> {
  const parent = getParentPublicPage(page, publicPagesById);
  const resolved = {} as Record<LanguageKeys, string>;
  for (const lang of LANGUAGE_KEYS) {
    resolved[lang] = buildFullPathFromSegment(segments[lang], parent, lang);
  }
  return resolved;
}
